import { useEffect, useRef, useState } from 'react';
import { OPENABLE, STATS } from '../editor/buffers';
import { systemInfo } from '../editor/telemetry';

// Boot sequence shown between the intro and the editor. It does real work:
// project screenshots are preloaded, and the bar tracks how many have arrived.
const MIN_MS = 1500;
const MAX_MS = 4500;
const BAR = 32;

function preload(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = img.onerror = () => resolve();
        img.src = src;
        if (img.complete) resolve();
    });
}

export default function Boot({ theme, onDone }) {
    const images = useRef(OPENABLE.filter((b) => b.image).map((b) => b.image));
    const [loaded, setLoaded] = useState(0);
    const [elapsed, setElapsed] = useState(0);
    const [leaving, setLeaving] = useState(false);
    const done = useRef(false);
    const sysRef = useRef(systemInfo());

    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const minMs = reduced ? 300 : MIN_MS;

    const finish = useRef(() => {});
    finish.current = () => {
        if (done.current) return;
        done.current = true;
        setLeaving(true);
        setTimeout(onDone, reduced ? 0 : 380);
    };

    useEffect(() => {
        images.current.forEach((src) => preload(src).then(() => setLoaded((n) => n + 1)));
        const start = performance.now();
        let raf = 0;
        const tick = (now) => {
            setElapsed(now - start);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        const skip = () => finish.current();
        window.addEventListener('keydown', skip);
        window.addEventListener('pointerdown', skip);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('keydown', skip);
            window.removeEventListener('pointerdown', skip);
        };
    }, []);

    const total = images.current.length || 1;
    const assets = loaded / total;
    // Progress is paced by time but can't outrun the real image loading.
    const timeP = Math.min(1, elapsed / minMs);
    const progress = elapsed >= MAX_MS ? 1 : Math.min(timeP, 0.15 + assets * 0.85);

    useEffect(() => {
        if (progress >= 1) finish.current();
    }, [progress]);

    // Log lines appear as progress passes their threshold.
    const files = OPENABLE.filter((b) => !b.path.startsWith('projects/'));
    const sys = sysRef.current;
    const hw = [sys.cores && `${sys.cores} cores`, sys.memory && `${sys.memory} GB`].filter(Boolean).join(' · ');
    const log = [
        { at: 0, text: <>adeboye.dev <span className="dimmed">v{new Date().getFullYear()}.{STATS.projects}</span></> },
        hw && { at: 0.02, ok: true, text: <>probing hardware <span className="b-num">{hw}</span></> },
        { at: 0.03, ok: true, text: <>display <span className="b-num">{sys.viewport}</span> @{sys.dpr}x <span className="dimmed">· {sys.os} · {sys.browser}</span></> },
        sys.network && { at: 0.04, ok: true, text: <>network <span className="b-num">{sys.network}</span></> },
        { at: 0.05, ok: true, text: <>mounting <span className="b-path">~/adeboye</span></> },
        ...files.map((b, i) => ({ at: 0.08 + i * 0.05, ok: true, text: <>reading <span className="b-path">{b.path}</span></> })),
        {
            at: 0.36,
            ok: loaded >= total,
            text: (
                <>
                    fetching screenshots <span className="b-num">{Math.min(loaded, total)}/{total}</span>
                </>
            ),
        },
        { at: 0.6, ok: true, text: <>indexing <span className="b-num">{STATS.projects}</span> projects · <span className="b-num">{STATS.tools}</span> tools</> },
        { at: 0.74, ok: true, text: <>loading theme <span className="b-hot">{theme.name}</span> <span className="dimmed">— {theme.description.toLowerCase()}</span></> },
        { at: 0.88, ok: true, text: <>starting editor</> },
    ].filter((l) => l && progress >= l.at);

    const filled = Math.round(progress * BAR);
    const pct = String(Math.round(progress * 100)).padStart(3, ' ');

    return (
        <main className={`boot ${leaving ? 'is-leaving' : ''}`} aria-busy={!leaving} aria-label="Loading the editor">
            <div className="boot-inner">
                <p className="boot-brand">
                    <span className="brand-mark">ab</span>
                    <span>adeboye.bello</span>
                </p>
                <ol className="boot-log">
                    {log.map((l, i) => (
                        <li key={i} className="boot-line">
                            {l.ok === undefined ? (
                                <span className="b-tag dimmed">{'      '}</span>
                            ) : l.ok ? (
                                <span className="b-tag b-ok">[ ok ]</span>
                            ) : (
                                <span className="b-tag b-wait">[ .. ]</span>
                            )}{' '}
                            {l.text}
                        </li>
                    ))}
                    <li className="boot-line boot-caret" aria-hidden="true">
                        <span className="t-cursor" />
                    </li>
                </ol>
                <p className="boot-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
                    <span className="on">{'█'.repeat(filled)}</span>
                    <span className="off">{'░'.repeat(BAR - filled)}</span>
                    <span className="b-pct">{pct}%</span>
                </p>
                <p className="boot-skip dimmed">press any key to skip</p>
            </div>
            <p className="statusline boot-status" aria-hidden="true">
                <span className="mode">LOADING</span>
                <span className="path">~/adeboye/README.md</span>
                <span className="meta">{Math.min(loaded, total)}/{total} assets</span>
            </p>
        </main>
    );
}
